import { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useIsMutating } from '@tanstack/react-query';
import { BsCheckCircleFill, BsCreditCardFill, BsPeopleFill, BsSendFill, BsStack, BsXCircleFill } from 'react-icons/bs';

import { StatCard } from '#emailCampaignFeature/components/common/CampaignStatsCards/CampaignStatsCards';
import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import Tabs from '#globalComponents/Tabs/Tabs';
import { useAuth } from '#globalContexts/AuthProvider';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { useUrlTab } from '#globalHooks/useUrlTab';
import { useCompanyProfile } from '#settingsFeature/models/companyProfile/queries';

import CostConfirmationModal from '../../components/campaigns/CostConfirmationModal';
import PostMailingAdvancedTab from '../../components/campaigns/detail/PostMailingAdvancedTab';
import PostMailingOverviewTab from '../../components/campaigns/detail/PostMailingOverviewTab';
import PostMailingRecipientsTab from '../../components/campaigns/detail/PostMailingRecipientsTab';
import PostMailingStatisticsTab from '../../components/campaigns/detail/PostMailingStatisticsTab';
import PostMailingStatusTimeline from '../../components/campaigns/detail/PostMailingStatusTimeline';
import PostMailingStatusBadge from '../../components/campaigns/PostMailingStatusBadge';
import { designHasContent, designHasInvalidQrLink } from '../../constants/pmDesign';
import { PostMailingDetailsProvider, usePostMailingDetailsContext } from '../../contexts/PostMailingDetailsContext';
import { postMailingCampaignsMutationKeys } from '../../models/campaigns/keys';
import {
	useConfirmAndSendPostMailing,
	useCancelPostMailingCampaign,
	useAbandonPostMailingPayment,
	useResumePostMailingPayment,
} from '../../models/campaigns/mutations';
import { usePostMailingCostEstimate } from '../../models/campaigns/queries';

const TABS = [
	{ key: 'overview', label: 'Übersicht' },
	{ key: 'recipients', label: 'Empfänger' },
	{ key: 'statistics', label: 'Statistik' },
	{ key: 'advanced', label: 'Erweitert' },
];
const TAB_KEYS = TABS.map((tab) => tab.key);

const CANCELLABLE_STATUSES = ['sending', 'needs_review', 'ready_to_distribute', 'awaiting_offer'];

/**
 * Why sending is unavailable, or an empty string when it is possible. The capability check comes
 * first: an abo without letter sending cannot be fixed by completing the pre-flight list.
 * @param {object} state
 * @param {boolean} state.hasSendCapability
 * @param {string[]} state.prereqIssues
 * @param {boolean} state.hasSenderInfo
 * @returns {string}
 */
const buildSendBlockedReason = ({ hasSendCapability, prereqIssues, hasSenderInfo }) => {
	if (!hasSendCapability) return 'Briefversand ist in Ihrem aktuellen Abo nicht verfügbar';
	if (prereqIssues.length > 0) return 'Bitte vervollständigen Sie zuerst die offenen Punkte';
	if (!hasSenderInfo) return 'Bitte hinterlegen Sie zuerst Ihre Absenderdaten';
	return '';
};

/**
 * Visible pre-flight list for sending (the minimum-quantity check happens in the cost modal, the
 * sender-info gap has its own role-aware banner).
 * @param {object} campaign
 * @returns {string[]}
 */
const buildPrereqIssues = (campaign) => {
	const hasLists = (campaign.postMailingCampaign_contactList?.length ?? 0) > 0;
	const hasDesign = Boolean(
		campaign.postMailingTemplates || (campaign.designData && Object.keys(campaign.designData).length > 0),
	);
	// Template wins over the campaign's own designData, mirroring the backend's send-time resolution.
	const effectiveDesignData = campaign.postMailingTemplates?.designData ?? campaign.designData;
	const hasContent = designHasContent(effectiveDesignData);
	return [
		!hasLists && 'Mindestens eine Empfängerliste auswählen (Übersicht → Empfängerlisten).',
		!hasDesign && 'Eine Brief-Vorlage zuweisen (Übersicht → Design).',
		hasDesign && !hasContent && 'Das Brief-Design ist leer — bitte Inhalt in der Vorlage ergänzen.',
		hasContent &&
			designHasInvalidQrLink(effectiveDesignData) &&
			'Ein QR-Code hat keinen gültigen Ziel-Link — bitte in der Vorlage einen Link mit https:// hinterlegen.',
	].filter(Boolean);
};

const PostMailingDetailsPageContent = () => {
	const navigate = useNavigate();
	const { id: campaignId } = useParams();
	const [activeTab, setActiveTab] = useUrlTab(TAB_KEYS, 'overview');
	const [showCostModal, setShowCostModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);

	const { campaign, loadingCampaign, selectedListIds } = usePostMailingDetailsContext();

	const { orgRole } = useAuth();
	const canEditCompanyProfile = orgRole === 'owner' || orgRole === 'admin';
	const { data: companyProfile, isLoading: loadingProfile } = useCompanyProfile();

	const {
		data: costData,
		isLoading: loadingCost,
		isError: costEstimateFailed,
		refetch: refetchCostEstimate,
	} = usePostMailingCostEstimate(campaignId, {
		enabled: showCostModal,
	});
	const confirmAndSend = useConfirmAndSendPostMailing();
	const cancelCampaign = useCancelPostMailingCampaign();
	const abandonPayment = useAbandonPostMailingPayment();
	const resumePayment = useResumePostMailingPayment();
	const isSaving = useIsMutating({ mutationKey: postMailingCampaignsMutationKeys.syncLists }) > 0;
	const { hasCapability: hasSendCapability } = useCapabilityCheck('postmailing.send');

	if (loadingCampaign) {
		return (
			<Page header={<PageHeader breadcrumbs={[{ label: 'Post-Mailing', to: '/post-mailing' }, '...']} />}>
				<div>
					<div className="flex justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
					</div>
				</div>
			</Page>
		);
	}

	if (!campaign) {
		return (
			<Page
				header={<PageHeader breadcrumbs={[{ label: 'Post-Mailing', to: '/post-mailing' }, 'Nicht gefunden']} />}
			>
				<div>
					<div className="text-center py-12">
						<p className="text-bgBase-800">Kampagne nicht gefunden.</p>
						<Button variant="outline" className="mt-4" onClick={() => navigate('/post-mailing')}>
							Zurück zur Übersicht
						</Button>
					</div>
				</div>
			</Page>
		);
	}

	const canSend = campaign.status === 'draft';
	const canCancel = CANCELLABLE_STATUSES.includes(campaign.status);
	const needsReview = campaign.status === 'needs_review';
	const awaitingPayment = campaign.status === 'awaiting_payment';
	// awaiting_offer never froze recipients, so there are no sends to show either.
	const hasSends = !['draft', 'awaiting_offer'].includes(campaign.status);

	// Sender line on every letter resolves {{org.name/street/postal/city}} — all four must be set.
	const hasSenderInfo = Boolean(
		companyProfile?.name && companyProfile?.street && companyProfile?.postal && companyProfile?.city,
	);
	const prereqIssues = buildPrereqIssues(campaign);
	const sendBlockedReason = buildSendBlockedReason({ hasSendCapability, prereqIssues, hasSenderInfo });
	const sendDisabled = Boolean(sendBlockedReason);

	const handleConfirmSend = async () => {
		try {
			await confirmAndSend.mutateAsync(campaignId);
			setShowCostModal(false);
		} catch {
			// Error toast handled by the global MutationCache.
		}
	};

	const handleCancelCampaign = async () => {
		try {
			await cancelCampaign.mutateAsync(campaignId);
			setShowCancelModal(false);
		} catch {
			// Error toast handled by the global MutationCache.
		}
	};

	return (
		<Page
			header={
				<PageHeader breadcrumbs={[{ label: 'Post-Mailing', to: '/post-mailing' }, campaign.name]}>
					<PostMailingStatusBadge status={campaign.status} />
					{isSaving && <span className="text-xs text-bgBase-800">Speichert…</span>}
					{canSend && (
						<Button
							variant="solid"
							size="sm"
							onClick={() => setShowCostModal(true)}
							disabled={sendDisabled}
							title={sendBlockedReason}
						>
							<BsSendFill className="mr-2 h-4 w-4" />
							Weiter zum Versenden
						</Button>
					)}
					{awaitingPayment && (
						<>
							<Button
								variant="solid"
								size="sm"
								onClick={() => resumePayment.mutate(campaignId)}
								loading={resumePayment.isPending}
								disabled={abandonPayment.isPending || !hasSendCapability}
								title={
									hasSendCapability ? '' : 'Briefversand ist in Ihrem aktuellen Abo nicht verfügbar'
								}
							>
								<BsCreditCardFill className="mr-2 h-4 w-4" />
								Zahlung fortsetzen
							</Button>
							<Button
								variant="danger"
								size="sm"
								onClick={() => abandonPayment.mutate(campaignId)}
								loading={abandonPayment.isPending}
								disabled={resumePayment.isPending}
							>
								<BsXCircleFill className="mr-2 h-4 w-4" />
								Zahlung abbrechen
							</Button>
						</>
					)}
					{canCancel && (
						<Button
							variant="danger"
							size="sm"
							onClick={() => setShowCancelModal(true)}
							loading={cancelCampaign.isPending}
						>
							<BsXCircleFill className="mr-2 h-4 w-4" />
							Stornieren
						</Button>
					)}
				</PageHeader>
			}
		>
			<div>
				{/* Payment → production → expected-delivery timeline */}
				<PostMailingStatusTimeline status={campaign.status} sends={campaign.postMailingSends} />

				{/* Address-warning review banner: eBrief flagged some letters for manual confirmation */}
				{needsReview && (
					<Alert
						tone="caution"
						title="Prüfung erforderlich"
						actionLabel="Zur Prüfung"
						onAction={() => setActiveTab('statistics')}
						className="mb-6"
					>
						Einige Briefe haben eine zweifelhafte oder unvollständige Anschrift. Bitte geben Sie diese frei
						oder überspringen Sie sie, damit der Versand fortgesetzt werden kann.
					</Alert>
				)}

				{canSend && prereqIssues.length > 0 && (
					<Alert tone="caution" title="Bevor Sie versenden können" className="mb-6">
						<ul className="list-disc pl-4 space-y-0.5">
							{prereqIssues.map((issue) => (
								<li key={issue}>{issue}</li>
							))}
						</ul>
					</Alert>
				)}

				{canSend && !loadingProfile && !hasSenderInfo && (
					<Alert
						tone="caution"
						title="Absendeinformationen erforderlich"
						actionLabel={canEditCompanyProfile ? 'Zu den Einstellungen' : undefined}
						onAction={canEditCompanyProfile ? () => navigate('/settings?tab=company') : undefined}
						className="mb-6"
					>
						{canEditCompanyProfile
							? 'Für den Briefversand werden Firmenname und Adresse als Absender benötigt. Bitte hinterlegen Sie diese in den Firmeneinstellungen.'
							: 'Für den Briefversand werden Firmenname und Adresse als Absender benötigt. Bitte wenden Sie sich an einen Administrator Ihrer Organisation, damit diese in den Firmeneinstellungen hinterlegt werden.'}
					</Alert>
				)}

				{/* Stat cards */}
				<div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-4">
					<StatCard
						title="Empfänger"
						value={campaign.recipientCount ?? 0}
						icon={BsPeopleFill}
						iconColor="text-primary-700"
						bgColor="bg-primary-200"
					/>
					<StatCard
						title="Listen"
						value={campaign.postMailingCampaign_contactList?.length ?? 0}
						icon={BsStack}
						iconColor="text-primary-600"
						bgColor="bg-primary-100"
					/>
					<StatCard
						title="Bei der Post"
						value={campaign.sendStats?.sent ?? 0}
						icon={BsCheckCircleFill}
						iconColor="text-success-700"
						bgColor="bg-success-200"
					/>
				</div>

				<Tabs
					tabs={TABS.map(({ key, label }) => ({ id: key, label }))}
					activeId={activeTab}
					onChange={setActiveTab}
					className="mb-5"
				/>

				{activeTab === 'overview' && <PostMailingOverviewTab />}
				{activeTab === 'recipients' && <PostMailingRecipientsTab selectedListIds={selectedListIds} />}
				{activeTab === 'statistics' && (
					<PostMailingStatisticsTab
						campaignId={campaignId}
						campaignName={campaign.name}
						hasSends={hasSends}
						campaignStatus={campaign.status}
						ebriefJobId={campaign.ebriefJobId}
						designData={
							campaign.designData && Object.keys(campaign.designData).length > 0
								? campaign.designData
								: campaign.postMailingTemplates?.designData
						}
					/>
				)}
				{activeTab === 'advanced' && <PostMailingAdvancedTab />}
			</div>

			<CostConfirmationModal
				isOpen={showCostModal}
				onClose={() => setShowCostModal(false)}
				onConfirm={handleConfirmSend}
				campaignId={campaignId}
				listIds={selectedListIds}
				customVariableFallbacks={campaign.customVariableFallbacks}
				costData={costData}
				isLoading={loadingCost}
				isError={costEstimateFailed}
				onRetry={refetchCostEstimate}
				isSending={confirmAndSend.isPending}
			/>

			<ConfirmModal
				isOpen={showCancelModal}
				onClose={() => setShowCancelModal(false)}
				onConfirm={handleCancelCampaign}
				title="Kampagne stornieren"
				message={`Möchten Sie die Kampagne "${campaign.name}" wirklich stornieren? Bereits eingeplante Briefe werden beim Briefversand-Dienst zurückgezogen.`}
				confirmText="Kampagne stornieren"
				cancelText="Abbrechen"
				isLoading={cancelCampaign.isPending}
			/>
		</Page>
	);
};

const PostMailingDetailsPage = () => {
	const { id } = useParams();

	return (
		<PostMailingDetailsProvider campaignId={id}>
			<PostMailingDetailsPageContent />
		</PostMailingDetailsProvider>
	);
};

export default PostMailingDetailsPage;
